// SPDX-License-Identifier: GPL-3.0-or-later
// Randy, the rate-limit SigmaBot: Token Burn keeper (THEME.md section 5.5).
// A small bot with a cap. A rotor turns over his head. When the meter is full he stops the pop
// bumpers for 5.0 s. A ring shows the time left.

import { BoxGeometry, CylinderGeometry, type Mesh, TorusGeometry } from "three";
import { clamp, numberArg } from "../animation.ts";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawTextPanel } from "../art/labels.ts";
import { addBot } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  outlined,
  put,
  solid,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Randy. */
export const RANDY_SPEC: SigmaBotSpec = {
  body: "tiny",
  palette: "slate",
  eyes: "sleepy",
  mouth: "flat",
  hat: "cap",
  accessory: "none",
};

/** Size of one body unit of Randy. */
const SCALE = 1.3;

/** The pause time in seconds. The ring arc goes from 2 pi to 0 over this time. */
export const RANDY_WAIT_S = 5;
/** Arm turn speed in rad/s when the bumpers run. */
export const RANDY_ARM_SPEED = 0.5;
/** Ring segments per full circle. The arc is rounded to this step, so the geometry changes rarely. */
const RING_STEPS = 48;

/** The label text for a time left in seconds. */
export function randyLabel(secondsLeft: number): string {
  return secondsLeft > 0 ? `WAIT ${Math.ceil(secondsLeft)}` : "RATE OK";
}

/** The ring steps for the time left (seconds): 0 when there is no wait, else 1 to RING_STEPS. */
function ringStepsFor(secondsLeft: number): number {
  if (secondsLeft <= 0) return 0;
  return Math.max(1, Math.round(clamp(secondsLeft / RANDY_WAIT_S, 0, 1) * RING_STEPS));
}

/** Show the ring with an arc of `steps` steps, or hide it when there are none. */
function showRing(ring: Mesh<TorusGeometry>, steps: number): void {
  ring.visible = steps > 0;
  if (steps <= 0) return;
  ring.geometry.dispose();
  ring.geometry = new TorusGeometry(18, 2, 8, RING_STEPS, (steps / RING_STEPS) * 2 * Math.PI);
}

export function buildRandy(env: CharacterEnv): Built {
  const group = namedGroup("Randy");
  addBot(group, "RandyBot", RANDY_SPEC, SCALE);
  // A short post holds the rotor over his cap.
  group.add(
    outlined(put(new CylinderGeometry(2.5, 2.5, 14, 12), solid(BRAND_HEX.gray888), 0, 56, 0)),
  );

  const arms = namedGroup("RandyArms");
  arms.position.set(0, 62, 0);
  for (let i = 0; i < 3; i++) {
    const spoke = namedGroup(`RandySpoke${i}`);
    spoke.rotation.y = (i * 2 * Math.PI) / 3;
    spoke.add(outlined(put(new BoxGeometry(40, 5, 5), solid(BRAND_HEX.gray888), 20, 0, 0)));
    arms.add(spoke);
  }
  group.add(arms);
  group.add(outlined(put(new CylinderGeometry(14, 14, 3, 24), solid(BRAND_HEX.mark), 0, 58.5, 0)));

  const ring = put(
    new TorusGeometry(18, 2, 8, RING_STEPS, 2 * Math.PI),
    solid(BRAND_HEX.edge),
    0,
    67,
    0,
  );
  ring.rotation.x = Math.PI / 2;
  ring.name = "RandyRing";
  ring.visible = false;
  group.add(ring);

  let labelText = randyLabel(0);
  const label = makeLabel(env, 40, 12, 256, 80, (ctx, w, h) =>
    drawTextPanel(ctx, w, h, [labelText]),
  );
  label.mesh.position.set(0, 84, 0);
  group.add(label.mesh);

  let left = 0;
  let armAngle = 0;
  let lastTime = Number.NaN;
  let ringSteps = -1;
  return {
    group,
    tick(timeMs) {
      const a = motion(env);
      const dt = Number.isNaN(lastTime) ? 0 : clamp((timeMs - lastTime) / 1000, 0, 0.1);
      lastTime = timeMs;
      // The arms stop while the bumpers are stopped. With reduced motion they do not turn.
      if (left <= 0) armAngle += RANDY_ARM_SPEED * dt * a;
      arms.rotation.y = armAngle;

      const steps = ringStepsFor(left);
      if (steps !== ringSteps) {
        ringSteps = steps;
        showRing(ring, steps);
      }
      const text = randyLabel(left);
      if (text !== labelText) {
        labelText = text;
        label.redraw((ctx, w, h) => drawTextPanel(ctx, w, h, [labelText]));
      }
    },
    play(name, arg) {
      if (name === "wait") left = clamp(numberArg(arg, 0), 0, RANDY_WAIT_S);
    },
    dispose() {
      disposeTree(group);
    },
  };
}
